import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { relativeTime } from "../../lib/time";
import type { DuplicateSide } from "../../lib/crm-types";
import type { Cluster } from "../../lib/duplicate-clusters";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";
import { CompanyLink } from "./CompanyLink";
import { CompanyPicker } from "./CompanyPicker";
import { Input } from "./fields";

/**
 * Everything flagged on one domain, and the single question it is asking.
 *
 * This replaces a grouping panel and a stack of pair cards, which between them
 * asked one question many times over. Four unfiled Rexel depots are six flags,
 * so the page drew one panel listing all four twice -- once to choose the
 * group, once to tick the branches -- and then six cards, each restating two of
 * the same four names under the heading "Same website domain" and each offering
 * its own Merge and Not duplicates. Ten companies filled three screens, every
 * name appeared up to four times, and nothing on the page said which of the
 * controls was the one to use.
 *
 * The mess was structural rather than cosmetic: a *pair* is the unit the
 * detector records and the wrong unit to decide in. Nobody looks at four depots
 * and answers six questions about them. They look once and conclude one of
 * three things:
 *
 *   - they are branches of one group,
 *   - two of them are the same record entered twice,
 *   - they are different companies that share a domain.
 *
 * So the card asks that, once, and the three answers are three buttons. The
 * list of records never moves: choosing an answer grows the controls that
 * answer needs on the rows already on screen, rather than opening a second
 * list somewhere else. Each one resolves every flag in the cluster, which is
 * what makes six flags one decision.
 *
 * The shape of the two real answers is deliberately identical -- **pick one
 * record, and the rest follow it** -- because they are the same sentence with
 * a different verb: branches go *under* the one you pick, a duplicate is folded
 * *into* it. One list, one control, and "leave out" for the record that turns
 * out not to belong to either.
 *
 * Nothing here is automatic. Grouping is reversible and anybody's; merging is
 * a manager's, asks which record survives before it asks anything else, and
 * says out loud that it cannot be undone.
 */

type Answer = "group" | "merge" | "apart";

export function DuplicateCluster({
  cluster,
  canMerge,
  focusCompanyId,
  onChange,
}: {
  cluster: Cluster;
  /** Merging is admin-only; the other two answers are anybody's. */
  canMerge: boolean;
  /** The company whose page this is, when it has one. */
  focusCompanyId?: number;
  onChange: () => void;
}) {
  const { companies, flags, matchedValue } = cluster;

  const [answer, setAnswer] = useState<Answer | null>(null);
  /** The record the rest follow: the group, or the one a merge keeps. */
  const [picked, setPicked] = useState<number | null>(null);
  /** Records somebody has struck off. Everything else follows. */
  const [left, setLeft] = useState<number[]>([]);
  /**
   * Where the group comes from, kept apart from which company it is. Three
   * radios over one nullable id could not tell "nothing chosen yet" from "a
   * new one, please", and showed two of them ticked at once.
   */
  const [source, setSource] = useState<"listed" | "elsewhere" | "new">("listed");
  /** A group that is not in the cluster, found by search. */
  const [elsewhere, setElsewhere] = useState<DuplicateSide | null>(null);
  const [groupName, setGroupName] = useState("");

  /*
   * The record that is already a group, if there is one, which is the answer
   * nearly every one of these wants. Ties go to the biggest: a domain with two
   * half-built groups on it has one that is plainly the real one, and it is the
   * one with the depots in it.
   *
   * Without this the panel used to propose a brand-new group named after the
   * first record in the list -- so CEF-the-group was asked to become a branch
   * of a second CEF, which the two-level rule refuses.
   */
  const suggested = useMemo(() => {
    const groups = companies
      .filter((one) => one.parentCompanyId === null && one.branchCount > 0)
      .sort((a, b) => b.branchCount - a.branchCount);
    return groups[0] ?? null;
  }, [companies]);

  const open = flags.some((flag) => flag.status === "open");

  /* The oldest flag on the domain. A cluster's flags arrive as its records are
     entered, so the earliest is when the desk first had reason to look. */
  const since = flags
    .map((flag) => flag.detectedAt)
    .sort()[0];

  const dismissAll = useMutation({
    mutationFn: async () => {
      for (const flag of flags) {
        if (flag.status === "open") await crmApi.dismissDuplicate(flag.id);
      }
    },
    onSuccess: onChange,
  });

  const group = useMutation({
    mutationFn: (input: { ids: number[]; parentId: number | null; name: string }) =>
      crmApi.groupCompanies({
        companyIds: input.ids,
        parentCompanyId: input.parentId,
        parentName: input.parentId === null ? input.name : undefined,
      }),
    onSuccess: () => {
      reset();
      onChange();
    },
  });

  /*
   * One merge per record folded in, in order, stopping at the first refusal.
   *
   * The API takes a pair because which record survives is the whole decision
   * and a route that inferred it would decide by accident. A cluster of three
   * that really is one company is two of those decisions with the same answer,
   * so it is asked once here and sent twice -- rather than making somebody
   * find the right pair card twice, which is what the page used to do.
   */
  const merge = useMutation({
    mutationFn: async (input: { keepId: number; foldIds: number[] }) => {
      for (const id of input.foldIds) {
        await crmApi.mergeCompanies(id, input.keepId);
      }
    },
    onSuccess: () => {
      reset();
      onChange();
    },
  });

  const busy = dismissAll.isPending || group.isPending || merge.isPending;

  function reset(): void {
    setAnswer(null);
    setPicked(null);
    setLeft([]);
    setSource("listed");
    setElsewhere(null);
    setGroupName("");
  }

  /*
   * Opening an answer chooses nothing on the person's behalf, with one
   * exception: a record that is already a group is the group, and saying so is
   * the whole reason the cluster could be read at all.
   *
   * It used to default to "a new group named <first record>" whenever no
   * existing group was found, which is the most consequential of the options
   * and reached without anybody selecting it -- a pair entered twice by two
   * reps was one click from becoming a group of two.
   */
  function choose(next: Answer): void {
    setAnswer(next);
    setPicked(next === "group" ? (suggested?.id ?? null) : null);
    setSource("listed");
    setLeft([]);
  }

  /** The record the others follow, once one has been chosen. */
  const master: DuplicateSide | null =
    answer === "group" && source === "elsewhere"
      ? elsewhere
      : answer === "group" && source === "new"
        ? null
        : (companies.find((one) => one.id === picked) ?? null);

  /* Already where it is being asked to go. Listed, but not as work to do. */
  const settled = (one: DuplicateSide): boolean =>
    answer === "group" && master !== null && one.parentCompanyId === master.id;

  const following = companies.filter(
    (one) =>
      one.id !== master?.id && !settled(one) && !left.includes(one.id),
  );

  /* What comes along with them: a joining record that is itself a group hands
     its branches over, and they end up beside it rather than under it. */
  const absorbing = following.reduce((total, one) => total + one.branchCount, 0);

  const naming = answer === "group" && source === "new";
  const blocked =
    following.length === 0 ||
    (answer === "group" && source === "elsewhere" && elsewhere === null) ||
    (answer === "group" && source === "listed" && master === null) ||
    (answer === "merge" && master === null) ||
    (naming && (following.length < 2 || groupName.trim() === ""));

  const error =
    problem(group.error) ?? problem(merge.error) ?? problem(dismissAll.error);

  return (
    <section className="rounded-lg border border-line bg-surface">
      {/*
        The evidence names the card. Every pair card used to be headed "Same
        website domain", which is the one thing that was true of all six of
        them and told a reader nothing; the domain itself is what they are
        looking at and what distinguishes one cluster from the next.
      */}
      <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-line px-3 py-2">
        <code className="rounded bg-surface-2 px-1.5 py-0.5 text-xs text-content">
          {matchedValue ?? "Same details"}
        </code>
        <span className="text-xs text-muted">
          {companies.length} records share this domain
        </span>
        {since ? (
          <span className="text-[11px] text-faint">
            flagged {relativeTime(since)}
          </span>
        ) : null}
        {open ? null : (
          <span className="ml-auto text-[11px] uppercase tracking-wide text-faint">
            {flags[0]?.status}
          </span>
        )}
      </header>

      <ul className="divide-y divide-line/60">
        {companies.map((one) => (
          <Row
            key={one.id}
            company={one}
            companies={companies}
            focusCompanyId={focusCompanyId}
            /* No control at all until an answer is chosen. A list that is
               only being read should not look like a form. */
            picking={answer === "group" || answer === "merge"}
            radioName={`master-${cluster.key}`}
            chosen={master?.id === one.id && source === "listed"}
            /* Two levels and no more, so a record already inside another group
               cannot become one. Offering it and then refusing is worse. */
            cannotLead={
              answer === "group" &&
              one.parentCompanyId !== null &&
              one.parentCompanyId !== master?.id
            }
            settled={settled(one)}
            leftOut={left.includes(one.id)}
            onPick={() => {
              setPicked(one.id);
              setSource("listed");
            }}
            onToggle={() =>
              setLeft(
                left.includes(one.id)
                  ? left.filter((id) => id !== one.id)
                  : [...left, one.id],
              )
            }
          />
        ))}
      </ul>

      {/*
        The group can be a company that is not in the cluster: a stray depot
        turns up on cef.co.uk months after CEF was built, and CEF is in none of
        these flags. Without this the only way to say so is to invent a second
        CEF. Offered under the list rather than inside it, because neither is a
        record that was flagged.
      */}
      {answer === "group" ? (
        <div className="flex flex-col gap-1.5 border-t border-line px-3 py-2">
          <label className="flex flex-wrap items-center gap-2 text-xs text-content">
            <input
              type="radio"
              name={`master-${cluster.key}`}
              checked={source === "elsewhere"}
              onChange={() => setSource("elsewhere")}
            />
            <span>A group that is not listed above</span>
            {source === "elsewhere" ? (
              elsewhere === null ? (
                <span className="w-56">
                  <CompanyPicker
                    canBeGroup
                    placeholder="Which group?"
                    exclude={companies.map((one) => one.id)}
                    onPick={(one) =>
                      setElsewhere({
                        id: one.id,
                        name: one.name,
                        tradeAccountNumber: one.tradeAccountNumber,
                        branchCount: one.branchCount,
                        parentCompanyId: one.parentCompanyId,
                      })
                    }
                  />
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CompanyLink id={elsewhere.id} name={elsewhere.name} />
                  <button
                    type="button"
                    onClick={() => setElsewhere(null)}
                    className="text-[11px] text-faint underline hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    change
                  </button>
                </span>
              )
            ) : null}
          </label>

          {/*
            Last, and named for what it costs. A group record is CRM-only -- a
            name and nothing else, no trade account and no link to the
            fulfilment platform -- so inventing one when a real group is sitting
            in the list above is how a domain ends up with two CEFs.
          */}
          <label className="flex flex-wrap items-center gap-2 text-xs text-content">
            <input
              type="radio"
              name={`master-${cluster.key}`}
              checked={source === "new"}
              onChange={() => setSource("new")}
            />
            <span>None of them — create a group called</span>
            <span className="w-56">
              <Input
                value={groupName}
                placeholder="Group name"
                onChange={(value) => {
                  setGroupName(value);
                  setSource("new");
                }}
              />
            </span>
          </label>
        </div>
      ) : null}

      {open ? (
        <footer className="border-t border-line px-3 py-2">
          {answer === null ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {/*
                The question, in the words somebody would use. "Which one is the
                group" was step 1 of a form that had already decided the answer
                was a group -- and for a pair entered twice by two reps, it is
                not.
              */}
              <span className="mr-1 text-xs text-muted">
                What are these {companies.length} records?
              </span>
              <Button size="sm" variant="secondary" onClick={() => choose("group")}>
                Branches of one group
              </Button>
              {canMerge ? (
                <Button size="sm" variant="secondary" onClick={() => choose("merge")}>
                  The same record, twice
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" onClick={() => choose("apart")}>
                Different companies
              </Button>
              {canMerge ? null : (
                <span className="ml-auto text-[11px] text-faint">
                  Entered twice? Merging them is a manager's.
                </span>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <p className="text-[11px] text-muted">{explain(answer, master, naming)}</p>

              {answer === "group" && absorbing > 0 ? (
                <p className="text-[11px] text-faint">
                  {absorbing} branch{absorbing === 1 ? "" : "es"} of a joining
                  group comes along, beside it rather than under it.
                </p>
              ) : null}

              {naming && following.length < 2 ? (
                <p className="text-[11px] text-faint">
                  A new group needs two companies in it.
                </p>
              ) : null}

              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <Button size="sm" variant="ghost" disabled={busy} onClick={reset}>
                  Cancel
                </Button>

                {answer === "apart" ? (
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => dismissAll.mutate()}
                  >
                    {dismissAll.isPending ? "Dismissing…" : "Stop asking"}
                  </Button>
                ) : answer === "merge" ? (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={busy || blocked}
                    onClick={() =>
                      merge.mutate({
                        keepId: master!.id,
                        foldIds: following.map((one) => one.id),
                      })
                    }
                  >
                    {merge.isPending
                      ? "Merging…"
                      : master === null
                        ? "Fold in"
                        : `Fold ${following.length} into ${master.name}`}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={busy || blocked}
                    onClick={() =>
                      group.mutate({
                        ids: following.map((one) => one.id),
                        parentId: master?.id ?? null,
                        name: groupName.trim(),
                      })
                    }
                  >
                    {group.isPending
                      ? "Filing…"
                      : master === null && !naming
                        ? "File as branches"
                        : `File ${following.length} under ${
                            naming
                              ? groupName.trim() || "a new group"
                              : (master?.name ?? "")
                          }`}
                  </Button>
                )}
              </div>
            </div>
          )}
        </footer>
      ) : null}

      {error ? (
        <p className="border-t border-line px-3 py-2 text-[11px] text-sla-breached">
          {error}
        </p>
      ) : null}
    </section>
  );
}

/**
 * One flagged record, reading the same whether or not it is being chosen.
 *
 * The radio appears in place when an answer is chosen rather than the list
 * being redrawn somewhere else, so the thing somebody has just read does not
 * move under them.
 */
function Row({
  company,
  companies,
  focusCompanyId,
  picking,
  radioName,
  chosen,
  cannotLead,
  settled,
  leftOut,
  onPick,
  onToggle,
}: {
  company: DuplicateSide;
  /** The rest of the cluster, so a parent can be named rather than numbered. */
  companies: DuplicateSide[];
  focusCompanyId?: number;
  picking: boolean;
  /** Shared with the two rows under the list: they are one choice. */
  radioName: string;
  chosen: boolean;
  cannotLead: boolean;
  settled: boolean;
  leftOut: boolean;
  onPick: () => void;
  onToggle: () => void;
}) {
  const parent =
    company.parentCompanyId === null
      ? null
      : (companies.find((one) => one.id === company.parentCompanyId)?.name ??
        `#${company.parentCompanyId}`);

  const body = (
    <>
      {picking ? (
        <input
          type="radio"
          name={radioName}
          className="shrink-0"
          checked={chosen}
          /* A record already filed under the chosen group is not a candidate
             to be one: two levels, and no more. */
          disabled={cannotLead || leftOut || settled}
          onChange={onPick}
        />
      ) : null}

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "text-sm",
            leftOut ? "text-faint line-through" : "text-content",
          )}
        >
          {company.id === focusCompanyId ? (
            company.name
          ) : (
            <CompanyLink id={company.id} name={company.name} />
          )}
        </span>
        <span className="ml-2 text-[11px] text-faint">
          #{company.id}
          {company.id === focusCompanyId ? " · this one" : ""}
          {company.tradeAccountNumber ? ` · ${company.tradeAccountNumber}` : ""}
          {company.branchCount > 0
            ? ` · already a group of ${company.branchCount}`
            : ""}
          {parent ? ` · already a branch of ${parent}` : ""}
        </span>
      </span>
    </>
  );

  return (
    <li className="flex items-center gap-2 px-3 py-1.5">
      {picking && !cannotLead && !settled ? (
        <label className="flex min-w-0 flex-1 items-center gap-2">{body}</label>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-2">{body}</span>
      )}

      {settled ? (
        <span className="shrink-0 text-[11px] text-faint">already filed here</span>
      ) : picking && !chosen ? (
        /* The escape hatch for the one record that does not belong with the
           rest: three depots and a customer whose website happens to sit on
           the same domain is a real cluster, and without this it has to be
           filed wrongly and then unpicked on a company page. */
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 text-[11px] text-faint hover:underline hover:text-brand-600 dark:hover:text-brand-400"
        >
          {leftOut ? "put back" : "leave out"}
        </button>
      ) : null}
    </li>
  );
}

/** What the button is about to do, in a sentence, before it is pressed. */
function explain(
  answer: Answer,
  master: DuplicateSide | null,
  naming: boolean,
): string {
  if (answer === "apart")
    return "They stay separate and the queue stops asking. Nothing is changed about either record.";

  if (answer === "merge")
    return master === null
      ? "Which record survives? Everything on the others — their people, tickets, activity and branches — moves onto it. This cannot be undone from the CRM."
      : `Everything on the others moves onto ${master.name}. Their rows stay reachable so old links work, but stop appearing anywhere. This cannot be undone from the CRM.`;

  if (naming)
    return "The new group is a CRM record: a name and nothing else. Each branch keeps its own people, tickets and rep.";

  return master === null
    ? "Which one is the group? The rest are filed under it as branches."
    : `The rest are filed under ${master.name} as branches. Nothing is merged — each keeps its own people, tickets and rep.`;
}

/** A server's own words where it gave any, and a plain sentence where it did not. */
function problem(cause: unknown): string | null {
  if (cause === null || cause === undefined) return null;
  return cause instanceof ApiError
    ? cause.message
    : "That did not work. Try again.";
}
